/**
 * Frontend helper for web push notifications.
 *
 * Handles:
 *   - Registering the service worker
 *   - Asking the browser for notification permission
 *   - Subscribing the browser to push and saving the subscription to Supabase
 *   - Unsubscribing
 *
 * All persistence is via the save_push_subscription / delete_push_subscription
 * RPCs (which run as the authenticated user, no service-role exposed).
 */
import { supabase, api } from './api';

// Public VAPID key (matches the private key set in Railway env vars)
// This is safe to expose in the frontend bundle.
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** Is web push supported on this browser? */
export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/** Current permission state (granted / denied / default) */
export function pushPermissionState() {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

/** Register the service worker (idempotent, safe to call on every page load) */
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.register('/sw.js');
  } catch (err) {
    console.error('[push] SW registration failed:', err);
    return null;
  }
}

/** Get the current push subscription (or null if not subscribed) */
export async function getCurrentSubscription() {
  if (!isPushSupported()) return null;
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
}

/**
 * Ask for permission, subscribe to push, and save the subscription on the
 * server. Returns { ok: true } on success, or { ok: false, reason } on failure.
 *
 * If permission has previously been denied, returns reason='denied' and the
 * caller should show instructions telling the user how to re-enable in their
 * browser settings.
 */
export async function enablePushNotifications() {
  if (!isPushSupported()) return { ok: false, reason: 'unsupported' };
  if (!VAPID_PUBLIC_KEY)  return { ok: false, reason: 'not_configured' };

  // Permission prompt
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return { ok: false, reason: permission === 'denied' ? 'denied' : 'dismissed' };
  }

  // Subscribe to push
  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    try {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
    } catch (err) {
      console.error('[push] subscribe failed:', err);
      return { ok: false, reason: 'subscribe_failed', error: err.message };
    }
  }

  // Persist the subscription server-side
  const json = sub.toJSON();
  const { data, error } = await supabase.rpc('save_push_subscription', {
    p_endpoint:   json.endpoint,
    p_p256dh:     json.keys.p256dh,
    p_auth:       json.keys.auth,
    p_user_agent: navigator.userAgent || null,
  });
  if (error || !data?.ok) {
    console.error('[push] Failed to save subscription server-side:', error);
    return { ok: false, reason: 'save_failed', error: error?.message };
  }

  // Fire the welcome push in the background — don't block the toggle UI
  // on a network round-trip. Server is idempotent so this is safe to call
  // every enable; first call sends, subsequent calls are no-ops.
  api.sendWelcomePush().catch(welcomeErr => {
    console.warn('[push] welcome push send failed (non-fatal):', welcomeErr);
  });

  return { ok: true };
}

/** Unsubscribe from push and remove the saved subscription server-side. */
export async function disablePushNotifications() {
  if (!isPushSupported()) return { ok: true };
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return { ok: true };

  const endpoint = sub.endpoint;
  try { await sub.unsubscribe(); } catch (_) {}
  await supabase.rpc('delete_push_subscription', { p_endpoint: endpoint });
  return { ok: true };
}
